import { useEffect, RefObject } from 'react';
import { shadowRoot } from 'app/shadowRoot';

interface UseDialogFocusOptions {
  isActive: boolean;
  containerRef: RefObject<HTMLElement>;
  currentPage?: number;
  onClose: () => void;
}

/**
 * Custom hook that manages dialog focus behavior including:
 * - Focus management for accessibility
 * - Focus trap for keyboard navigation
 * - Escape key handling
 */
export default function useDialogFocus({ isActive, containerRef, currentPage, onClose }: UseDialogFocusOptions) {
  // Focus management for accessibility
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive, containerRef]);

  // Escape key handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isActive) {
        onClose();
      }
    };

    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isActive, onClose]);

  // Focus trap for keyboard navigation
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const formElement = containerRef.current;
    const focusableElements = formElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (shadowRoot.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement?.focus();
        }
      } else {
        // Tab
        if (shadowRoot.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement?.focus();
        }
      }
    };

    formElement.addEventListener('keydown', handleTabKey);

    return () => {
      formElement.removeEventListener('keydown', handleTabKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, currentPage]);
}
